import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lovable-demo/runtime/client";
import { VerdictBadge } from "@/lovable-demo/components/bits";
import { AttachEvidenceButton } from "@/lovable-demo/components/evidence-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lovable-demo/lib/warranty/calc";
import {
  canChangeVerdict,
  SEEDED_VERDICT_LABEL,
  validateVerdictChange,
  verdictAuthorship,
  verdictChangeFromRow,
  verdictRuleKey,
  VERDICTS,
  type Verdict,
  type VerdictChange,
  type VerdictEvidence,
} from "@/lovable-demo/lib/warranty/compliance-verdict";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { useMyIdentity } from "@/lovable-demo/lib/warranty/use-pfmea-signoffs";
import type { ClauseMapping, ProblemRecord } from "@/lovable-demo/lib/warranty/types";

const historyKey = (recordId: string) => ["lovable-demo", "compliance-verdicts", recordId] as const;

export function useVerdictHistory(recordId: string) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: historyKey(recordId),
    queryFn: async (): Promise<VerdictChange[]> => {
      const { data, error } = await supabase
        .from("compliance_verdict_changes")
        .select("*")
        .eq("record_id", recordId)
        .order("created_at");
      if (error) throw new Error(error.message);
      return (data ?? []).map(verdictChangeFromRow);
    },
  });
  return {
    history: q.data ?? [],
    refresh: () => qc.invalidateQueries({ queryKey: historyKey(recordId) }),
  };
}

export function VerdictAuthorship({
  clause,
  record,
}: {
  clause: ClauseMapping;
  record: ProblemRecord;
}) {
  const a = verdictAuthorship(clause, record);
  return (
    <span className="num mt-1 block text-[0.64rem] text-muted-foreground">
      {a.seeded && (
        <span className="mr-1 rounded border border-border px-1 py-px text-[0.58rem] uppercase">
          {SEEDED_VERDICT_LABEL}
        </span>
      )}
      {a.seeded ? a.by : `Assessed by ${a.by}`}
      {a.title && !a.by.includes(a.title) ? `, ${a.title}` : ""} · {a.at ? fmtDate(a.at) : "—"} ·
      Authority: {a.rule}
    </span>
  );
}

export function VerdictHistory({ items }: { items: VerdictChange[] }) {
  if (!items.length) return null;
  return (
    <ul className="mt-1.5 space-y-1 border-l border-border pl-2">
      {[...items].reverse().map((h) => (
        <li key={h.id} className="text-[0.64rem] text-muted-foreground">
          <span className="num text-foreground/80">
            {h.oldVerdict} → {h.newVerdict}
          </span>{" "}
          · {h.assessedBy}
          {h.assessedByTitle && !h.assessedBy.includes(h.assessedByTitle)
            ? `, ${h.assessedByTitle}`
            : ""}{" "}
          · {fmtDate(h.at)} · {h.authorityRuleKey}
          <span className="block">“{h.rationale}”</span>
          {h.evidence?.kind && (
            <span className="block">
              Evidence:{" "}
              {h.evidence.kind === "file"
                ? h.evidence.fileName
                : `${h.evidence.reference} — ${h.evidence.description ?? ""}`}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ChangeVerdictButton({
  record,
  clause,
  onDone,
}: {
  record: ProblemRecord;
  clause: ClauseMapping;
  onDone: () => void;
}) {
  const { changeClauseVerdict } = useWarranty();
  const me = useMyIdentity();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState<Verdict | null>(null);
  const [rationale, setRationale] = useState("");
  const [evidence, setEvidence] = useState<VerdictEvidence | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setNext(null);
    setRationale("");
    setEvidence(null);
    setError(null);
  };
  const needsDirector = next
    ? verdictRuleKey(clause.verdict, next) === "compliance.verdict_violated"
    : clause.verdict === "Violated";
  const allowed = next ? canChangeVerdict(me.roles, clause.verdict, next) : true;

  const submit = async () => {
    if (!next) return setError("Choose a new verdict.");
    const invalid = validateVerdictChange(clause, next, rationale);
    if (invalid) return setError(invalid);
    setBusy(true);
    setError(null);
    try {
      const r = await changeClauseVerdict(record.id, clause.id, next, rationale, evidence);
      if (r.ok) {
        toast.success("Verdict recorded and confirmed", {
          description: `${clause.clause}: ${clause.verdict} → ${next}`,
        });
        setOpen(false);
        reset();
        onDone();
      } else {
        setError(r.error);
        toast.error("Verdict not changed", { description: r.error });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-[0.68rem] text-primary"
        onClick={() => setOpen(true)}
      >
        Change verdict
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (busy) return;
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change verdict — {clause.standard} {clause.clause}
            </DialogTitle>
            <DialogDescription>
              Current verdict: {clause.verdict}. Compliant or Gap may be set by a Quality Engineer
              or Quality Director. Setting Violated, or changing a verdict away from Violated,
              requires a Quality Director. Broadview verifies your authority and records the
              verdict, its audit entry and history together before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {VERDICTS.filter((v) => v !== clause.verdict).map((v) => (
              <Button
                key={v}
                type="button"
                size="sm"
                variant={next === v ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => {
                  setNext(v);
                  setError(null);
                }}
              >
                {v}
              </Button>
            ))}
          </div>
          {next && (
            <p className={cn("text-xs", allowed ? "text-muted-foreground" : "text-destructive")}>
              Authority rule: {verdictRuleKey(clause.verdict, next)} —{" "}
              {needsDirector ? "Quality Director only" : "Quality Engineer or Quality Director"}.
              {!allowed && " Your current role does not hold this authority."}
            </p>
          )}
          <div className="space-y-1.5">
            <label htmlFor={`verdict-rat-${clause.id}`} className="text-xs text-muted-foreground">
              Rationale (required)
            </label>
            <Textarea
              id={`verdict-rat-${clause.id}`}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              className="min-h-20 text-xs"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <AttachEvidenceButton
              recordId={record.id}
              gateId={`clause-${clause.id}`}
              requirement={`Evidence for ${clause.clause} verdict`}
              stageOnly
              onAttach={async (a) => {
                setEvidence(
                  a.kind === "file"
                    ? { kind: "file", fileName: a.fileName, filePath: a.filePath }
                    : { kind: "reference", reference: a.reference, description: a.description },
                );
                return { ok: true };
              }}
            />
            {evidence && (
              <span className="text-muted-foreground">
                {evidence.kind === "file" ? evidence.fileName : evidence.reference}
              </span>
            )}
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={submit}>
              {busy ? "Recording…" : "Record verdict"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ApBadge({ ap, className }: { ap: "H" | "M" | "L"; className?: string }) {
  const tone =
    ap === "H"
      ? "border-destructive/50 bg-destructive/15 text-destructive"
      : ap === "M"
        ? "border-warning/50 bg-warning/15 text-warning"
        : "border-success/50 bg-success/15 text-success";
  return (
    <span
      className={cn(
        "num inline-flex min-w-6 justify-center rounded border px-1.5 py-px text-xs font-semibold",
        tone,
        className,
      )}
      title={
        ap === "H"
          ? "Action Priority High"
          : ap === "M"
            ? "Action Priority Medium"
            : "Action Priority Low"
      }
    >
      {ap}
    </span>
  );
}

export { VerdictBadge };
